import { REDESIGN_COLORS } from '../styles/colors';

/**
 * Centralized configuration for the AI negotiation feature.
 *
 * Single source of truth for limits, timing, UI labels, and option
 * arrays shared across NegotiationContext, setup screen, chat screen,
 * list screen, and all negotiation sub-components.
 */

export const NEGOTIATION_CONFIG = {
  /** Maximum number of concurrent active negotiations per user */
  MAX_ACTIVE_NEGOTIATIONS: 3,

  /** Estimated hours per negotiation round (used for effort metrics) */
  HOURS_PER_ROUND: 1,

  /** Timeout (ms) before showing "taking longer than expected" in chat.
   *  Sized above observed Bedrock p50 (~12-14 s for full draft prompts) so
   *  the warning only fires on genuinely slow runs, not every generation. */
  DRAFT_GENERATION_TIMEOUT_MS: 20000,

  /** Hours since last send before showing the follow-up prompt */
  FOLLOW_UP_THRESHOLD_HOURS: 48,

  /** Business hours window (Europe/London) for scheduled sends */
  BUSINESS_HOURS_START: 9,
  BUSINESS_HOURS_END: 20,

  /** Maximum draft regenerations allowed per negotiation round */
  MAX_DRAFTS_PER_ROUND: 3,

  /** Maximum attachment size in bytes (3 MB) */
  MAX_ATTACHMENT_SIZE_BYTES: 3 * 1024 * 1024,

  /** AsyncStorage key for the beta banner dismissal flag */
  BETA_BANNER_STORAGE_KEY: 'neg_beta_banner_dismissed',
};

/**
 * Status label and color mapping for negotiation status pills.
 * Keys match the `negotiations.status` enum values in the database.
 */
export const STATUS_LABELS = {
  setup: {
    label: 'Setting up',
    color: REDESIGN_COLORS.textSecondary,
  },
  active: {
    label: 'Active',
    color: REDESIGN_COLORS.ctaBlue,
  },
  stalled: {
    label: 'Stalled',
    color: REDESIGN_COLORS.scoreMedium,
  },
  deal_reached: {
    label: 'Deal reached',
    color: REDESIGN_COLORS.scoreGood,
  },
  handed_off: {
    label: 'Handed off',
    color: REDESIGN_COLORS.scoreGood,
  },
  abandoned: {
    label: 'Abandoned',
    color: REDESIGN_COLORS.scorePoor,
  },
  closed: {
    label: 'Closed',
    color: REDESIGN_COLORS.textSecondary,
  },
  paused: {
    label: 'Paused',
    color: REDESIGN_COLORS.textSecondary,
  },
};

/** Negotiation priority options - actual negotiation levers the AI can use */
export const PRIORITY_OPTIONS = [
  { value: 'lowest_price', label: 'Lowest price', hint: 'Focus purely on getting the best price' },
  { value: 'warranty', label: 'Warranty included', hint: 'Ask dealer to include or extend warranty' },
  { value: 'free_delivery', label: 'Free delivery', hint: 'Save on transport costs' },
  { value: 'pre_sale_service', label: 'Full service before sale', hint: 'Request a service or MOT before handover' },
  { value: 'quick_completion', label: 'Quick completion', hint: 'Fast paperwork and collection' },
  { value: 'extras', label: 'Extras thrown in', hint: 'Spare key, fresh set of mats, dashcam fitted' },
];

/** Tone options for the strategy customisation section */
export const TONE_OPTIONS = [
  { value: 'balanced', label: 'Balanced' },
  { value: 'firm', label: 'Firm' },
  { value: 'friendly', label: 'Friendly' },
];

/** Urgency options for the strategy customisation section */
export const URGENCY_OPTIONS = [
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'urgent', label: 'Urgent' },
];

/** Funding method options for the strategy customisation section */
export const FUNDING_OPTIONS = [
  { value: 'undecided', label: 'Undecided' },
  { value: 'cash', label: 'Cash' },
  { value: 'finance', label: 'Finance' },
  { value: 'pcp', label: 'PCP' },
];

/** Outcome types for guided mode structured input (step 1) */
export const GUIDED_OUTCOME_TYPES = [
  { value: 'counter_offer', label: 'They made a counter-offer' },
  { value: 'accepted', label: 'They accepted my offer' },
  { value: 'rejected', label: 'They rejected my offer' },
  { value: 'no_response', label: 'No response yet' },
  { value: 'other', label: 'Something else' },
];

/**
 * Auto-pause reasons that the frontend recognises and maps to specific copy.
 * Values match `negotiations.auto_pause_reason` strings set by the worker.
 */
export const RECOGNISED_AUTO_PAUSE_REASONS = ['listing_removed'];

/**
 * Human-readable copy for each dealer signal classification.
 * Keys match VALID_LABELS in vps-negotiation-worker-dev/src/services/dealerReplyClassifier.js.
 * `auto_responder` is intentionally omitted — it is handled separately by the UI.
 */
export const DEALER_SIGNAL_COPY = {
  incremental_move: {
    title: 'Dealer making moves',
    description: 'They edged closer to your number. Press for the next concession.',
  },
  hardball: {
    title: 'Dealer holding firm',
    description: 'They are anchoring on a high price. Surface evidence and stay patient.',
  },
  final_offer_claim: {
    title: 'Final offer claim',
    description: 'Dealers often soften "final" offers when they sense walk-away. Test it.',
  },
  escalation_question: {
    title: 'Dealer asking back',
    description: 'They want commitment cues before moving. Stay non-committal until price improves.',
  },
  neutral: {
    title: 'Holding pattern',
    description: 'No new pricing signal. Time to apply gentle pressure or pivot.',
  },
  deal_reached: {
    title: 'Deal in reach',
    description: 'They have signalled agreement. Confirm the price in writing before moving on.',
  },
};

/**
 * AsyncStorage key prefix for FormBridge dismissal flags.
 * Full key is `${FORM_BRIDGE_DISMISSAL_KEY_PREFIX}${negotiation.id}`.
 */
export const FORM_BRIDGE_DISMISSAL_KEY_PREFIX = 'neg_form_bridge_dismissed:';

/** Communication channel options for guided mode structured input (step 3) */
export const CHANNEL_OPTIONS = [
  { value: 'phone', label: 'Phone call' },
  { value: 'in_person', label: 'In person' },
  { value: 'web_form', label: 'Web form' },
  { value: 'other', label: 'Other' },
];
